// ResetForgotPasswordComponent.js
import { useEffect, useRef, useState } from "react";
import Button from "@/components/common/Button";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useAuth, useI18N, useResetPassword } from "common/hooks";

import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { isPasswordOneNumberSymbolValid, isPasswordValid } from "common/utils";
import { BsCheckCircle } from "react-icons/bs";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import Checkmark from "@/components/common/Checkmark";
import Loader from "@/components/common/Loader";
import { useAtom } from "jotai";
import { emailAtom, resetForgotPasswordCodeAtom } from "@/store/atoms";
import { useCustomToast } from "@/hooks/useCustomToast";

interface EnterPasswordProps {
  onClose: () => void;
  title: string;
}

const ResetForgotPasswordComponent = ({
  onClose,
  title,
}: EnterPasswordProps) => {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { user } = useAuth();
  const { showModal } = useAuthModals();

  const showToast = useCustomToast();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  const [email] = useAtom(emailAtom);
  const [resetForgotPasswordCode] = useAtom(resetForgotPasswordCodeAtom);

  const { resetPassword, error, isLoading, result } = useResetPassword();

  //   const navigate = useNavigate({ from: "/" });

  useEffect(() => {
    if (result) {
      showToast(result.message, "success");

      //close existing modal
      onClose();

      // show login popup modal
      showModal(AuthModalScreen.EnterEmail);
    }
  }, [result, isDark]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("authPopupResetForgotPasswordError");
      AnalyticsService.trackMx("authPopupResetForgotPasswordError", {
        UserName: user?.name,
      });
      showToast(error.message, "error");
    }
  }, [error, isDark]);

  const handleClose = () => {
    AnalyticsService.trackGAEventHandler("authPopupResetForgotPasswordClose");
    AnalyticsService.trackMx("authPopupResetForgotPasswordClose", {});

    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  const handleSave = () => {
    console.log("handleSave");

    AnalyticsService.trackGAEventHandler("authPopupResetForgotPasswordSubmit");
    AnalyticsService.trackMx("authPopupResetForgotPasswordSubmit", {
      UserName: user?.name,
    });

    resetPassword({
      email,
      code: resetForgotPasswordCode,
      password: newPassword,
      confirmPassword,
    });
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div>
      {isLoading && <Loader />}

      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-200px)] overflow-y-auto"
        role="none"
      >
        <header className="bg-white dark:bg-primary-900  py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0 z-1000">
          <h1
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            tabIndex={0}
            ref={firstFocusRef} // Attach the ref here
          >
            {title}
          </h1>
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="w-full md:max-w-[70%] mx-auto mt-5">
            <div className="flex flex-col gap-4 mb-6" tabIndex={0}>
              <div className="flex flex-col gap-4 mb-2">
                <FloatingLabelInput
                  label="New Password"
                  required
                  type="Password"
                  value={""}
                  placeholder="New Password"
                  aria-invalid={errorMessage.length > 0 ? "true" : "false"}
                  onChange={handleNewPasswordChange}
                />
              </div>

              <div className="flex flex-col gap-4 mb-4">
                <FloatingLabelInput
                  label="Confirm Password"
                  required
                  type="Password"
                  value={""}
                  placeholder="Confirm Password"
                  aria-invalid={
                    confirmPassword !== newPassword ? "true" : "false"
                  }
                  onChange={handleConfirmPasswordChange}
                />
              </div>

              {confirmPassword.length > 0 &&
                newPassword.length > 0 &&
                confirmPassword === newPassword && (
                  <div className="flex items-center">
                    {/* {confirmPassword}={newPassword} */}
                    <BsCheckCircle
                      size={14}
                      color={isDark ? "#D3D3D9" : "#151A36"}
                    />
                    <span className="ml-2 button-small-medium" tabIndex={0}>
                      {i18n.get("passwordMatches")}
                    </span>
                  </div>
                )}
              {confirmPassword.length > 0 &&
                newPassword.length > 0 &&
                confirmPassword !== newPassword && (
                  <div className="flex-row items-center" tabIndex={0}>
                    <span className="text-danger button-small-medium">
                      {i18n.get("passwordsDontMatchPleaseTryAgain")}
                    </span>
                  </div>
                )}
              {errorMessage.length > 0 && (
                <span
                  className="text-danger mt-5 button-small-medium"
                  tabIndex={0}
                >
                  {errorMessage}
                </span>
              )}
            </div>

            <div className="mb-6 text-neutral-600 dark:text-primary-100 button-small-semi-bold">
              <span tabIndex={0}>{i18n.get("yourPwdShouldContain")}</span>

              <div className="flex items-center mb-3 mt-3" tabIndex={0}>
                <Checkmark
                  disabled={
                    newPassword.length < 6
                  }
                />
                <span className="ml-2 button-small-medium" tabIndex={0}>
                  {i18n.get("passwordAtLeast6Chars")}
                </span>
              </div>

              <div className="flex items-center" tabIndex={0}>
                <Checkmark
                  disabled={
                    !isPasswordOneNumberSymbolValid(newPassword)
                  }
                />
                <span className="ml-2 button-small-medium" tabIndex={0}>
                  {i18n.get("passwordOneSymbolOrNumber")}
                </span>
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="w-96 h-28 px-16 py-4 bg-white dark:bg-primary-900 flex-col justify-center items-center gap-4 inline-flex">
            <div className="w-96 justify-center items-center inline-flex">
              <Button
                className="grow"
                disabled={
                  isLoading ||
                  newPassword.length < 6 ||
                  confirmPassword.length < 6
                }
                onClick={() => {
                  console.log("update clicked", newPassword, confirmPassword);
                  if (newPassword !== confirmPassword) {
                    setErrorMessage(
                      i18n.get("passwordsDontMatchPleaseTryAgain")
                    );
                    return;
                  }
                  if (!isPasswordValid(newPassword)) {
                    setErrorMessage(i18n.get("passwordShouldContain"));
                    return;
                  }
                  setErrorMessage("");
                  handleSave();
                }}
              >
                {i18n.get("updatePassword")}
              </Button>
            </div>
            <div className="justify-start items-start inline-flex">
              <div className="justify-center items-center gap-1 flex">
                <button
                  onClick={handleClose}
                  className="flex items-center gap-1 button-small-semi-bold underline text-links dark:text-white"
                >
                  {i18n.get("cancel")}
                </button>
              </div>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default ResetForgotPasswordComponent;
